import React, { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react'
import type { Misconception, Feedback } from '../types'
import clsx from 'clsx'

interface MisconceptionAlertProps {
  misconception: Misconception
  feedback?: Feedback
}

const severityConfig = {
  high: { color: 'border-red-400 bg-red-50', badge: 'bg-red-100 text-red-700', icon: '🔴', label: '높음' },
  medium: { color: 'border-orange-400 bg-orange-50', badge: 'bg-orange-100 text-orange-700', icon: '🟡', label: '중간' },
  low: { color: 'border-yellow-400 bg-yellow-50', badge: 'bg-yellow-100 text-yellow-700', icon: '🟢', label: '낮음' },
}

export default function MisconceptionAlert({ misconception, feedback }: MisconceptionAlertProps) {
  const [expanded, setExpanded] = useState(false)
  const config = severityConfig[misconception.severity]

  return (
    <div className={clsx('border-l-4 rounded-r-xl p-4 transition-all', config.color)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1">
          <AlertTriangle size={16} className="mt-0.5 text-orange-500 flex-shrink-0" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={clsx('badge text-xs', config.badge)}>
                {config.icon} {misconception.id}
              </span>
              <span className="text-xs text-slate-500">심각도: {config.label}</span>
            </div>
            <p className="text-sm font-semibold text-slate-800 mt-1">{misconception.description_ko}</p>
            <p className="text-xs text-slate-500 mt-0.5">{misconception.description}</p>
          </div>
        </div>
        {feedback && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {/* Feedback (expandable) */}
      {feedback && expanded && (
        <div className="mt-3 pt-3 border-t border-current/20">
          <div className="flex items-start gap-2">
            <Lightbulb size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-slate-800">{feedback.title}</p>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{feedback.message}</p>
              {feedback.hint && (
                <div className="mt-2 flex items-start gap-1.5 bg-white/60 rounded-lg p-2">
                  <span className="text-xs font-semibold text-amber-600">💡 힌트:</span>
                  <span className="text-xs text-slate-600">{feedback.hint}</span>
                </div>
              )}
              {feedback.example && (
                <div className="mt-2 bg-white/70 rounded-lg p-2">
                  <p className="text-xs font-semibold text-slate-500 mb-1">예시:</p>
                  <pre className="text-xs text-slate-700 font-mono whitespace-pre-wrap">
                    {JSON.stringify(feedback.example, null, 2)}
                  </pre>
                </div>
              )}
              {feedback.next_step && (
                <div className="mt-2 text-xs text-primary-600">
                  📚 추천 복습: <span className="font-semibold">{feedback.next_step}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
